import axios, { AxiosError } from 'axios';
import { Mutex } from 'async-mutex';
import { registerAdditionalHeadersInterceptor, getConfig } from '@creditornot/converse-shared';
import { waitAsync, reconnectAfterMs, getTokenDataFromStorage, isTokenAboutToExpire, mapRawDataToTokenData, removeTokenDataFromStorage, setTokenDataToStorage, } from './utils';
import { apiUrlByEnv } from './constants';
const maxReconnectAttempts = 3;
// Fall back to a mutex for tests (running in node.js) & browsers that don't support Web Locks API
const authMutex = new Mutex();
const runExclusive = async (callback) => {
    if (!navigator.locks) {
        return authMutex.runExclusive(async () => await callback());
    }
    return navigator.locks.request('converse-auth', async () => await callback());
};
export const createAuthenticator = (getAppAccessToken) => {
    const axiosInstance = axios.create();
    const config = getConfig();
    registerAdditionalHeadersInterceptor({ hostName: config.envName, hostRevision: config.envVersion }, axiosInstance);
    /**
     * fn to POST AccessTokenGrant or RefreshTokenGrant to Converse's /token endpoint
     */
    const loginToConverse = async (grant, tries = 0) => {
        try {
            const response = axiosInstance.post(`${apiUrlByEnv[config.env]}/auth-api/v1/token`, { ...grant, workspaceId: config.workspaceId });
            return mapRawDataToTokenData((await response).data);
        }
        catch (error) {
            if (error instanceof AxiosError) {
                // do not retry if server is explicitly telling us to go away
                // any other conditions besides 401?
                if (error.response?.status === 401) {
                    throw error;
                }
            }
            const nextTry = tries + 1;
            if (nextTry > maxReconnectAttempts) {
                // stop retrying after reaching attempts limit
                throw error;
            }
            // TODO(CHAT-000) detect offline mode and wait until network is online again
            return waitAsync(reconnectAfterMs(nextTry)).then(() => loginToConverse(grant, nextTry));
        }
    };
    const createToken = async () => {
        const appToken = await getAppAccessToken();
        const tokenData = await loginToConverse({
            grantType: 'wolt_access_token',
            audience: `converse_${config.sdkType}`,
            accessToken: appToken,
            appId: config.sdkType === 'widget' ? `${config.workspaceId}-${config.appId}` : undefined,
        });
        setTokenDataToStorage(tokenData, config.sdkType);
        return tokenData;
    };
    const refreshAccessToken = async () => {
        const { refreshToken } = getTokenDataFromStorage(config.sdkType) ?? {};
        if (!refreshToken) {
            // no refresh token available — try to create a new token instead
            return createToken();
        }
        try {
            const tokenData = await loginToConverse({
                grantType: 'refresh_token',
                refreshToken,
            }).catch(error => {
                if (axios.isAxiosError(error) && error.response?.status === 401) {
                    // 401 here means that refresh token is no longer valid
                    // try to create a new token instead
                    return createToken();
                }
                // re-throw error
                return Promise.reject(error);
            });
            setTokenDataToStorage(tokenData, config.sdkType);
            return tokenData;
        }
        catch (error) {
            removeTokenDataFromStorage(config.sdkType);
            // re-throw error
            throw error;
        }
    };
    const getToken = async () => await runExclusive(async () => {
        let tokenData = getTokenDataFromStorage(config.sdkType) || (await createToken());
        if (isTokenAboutToExpire(tokenData)) {
            tokenData = await refreshAccessToken();
        }
        return tokenData.accessToken;
    });
    const maybeRefreshToken = async (expiringAccessToken) => await runExclusive(async () => {
        // check that the token we're trying to refresh is the same as the one in storage
        // otherwise we might be refreshing an already refreshed token
        // if so, just return the token from storage
        const tokenData = getTokenDataFromStorage(config.sdkType);
        if (expiringAccessToken &&
            tokenData &&
            tokenData.accessToken !== expiringAccessToken &&
            !isTokenAboutToExpire(tokenData)) {
            return tokenData.accessToken;
        }
        return (await refreshAccessToken()).accessToken;
    });
    return {
        audience: config.sdkType,
        getToken,
        maybeRefreshToken,
    };
};
let authenticator;
export const initAuthenticator = (getAppAccessToken) => {
    if (authenticator) {
        // eslint-disable-next-line no-console
        console.warn("Authenticator was already initialized. If you see this message, there's likely a problem in how you setup the SDK.");
    }
    authenticator = createAuthenticator(getAppAccessToken);
    // cleanup on shutdown
    return () => {
        authenticator = undefined;
    };
};
export const getAuthenticator = () => {
    if (!authenticator) {
        throw new Error('Authenticator is not initialized');
    }
    return authenticator;
};
//# sourceMappingURL=authenticator.js.map